import * as angular from 'angular';
import './styling.scss';
import * as template from './template.html';
import { CurrentUserService } from 'services/currentUser';
import { getMortgages } from 'common/source/utilities/policyInfoHelper';
import { ModalService } from 'common/source/services/modal';
import * as _ from 'lodash';
import { HippoApiService } from 'services/hippoApi';

// TODO: make tests

const module = angular.module('notify-lender', []);

interface NewMortgageDetails extends MortgageDetails {
    isNew?: boolean;
}

interface MortgagesCollection {
    [key: string]: NewMortgageDetails | null;
}

interface NotifyLenderController extends angular.IStrictController {
    showAddLenderButton(): boolean;
    mortgages: MortgagesCollection;
    showLendersList: boolean;
    addLender: () => void;
    editLender: (index: number) => void;
    deleteLender: (index: number) => void;
    notifyLender: () => void;
    selectedLender: NewMortgageDetails;
    selectedLenderEscrowDetails: EscrowDetails;
    saveLenderInfo: () => void;
    cancelLenderInfo: () => void;
    policyInfo: CustomerPolicyInfo;
    tooltipText: string;
    showSpinner: boolean;
    getEscrowDetailsFromLender: (lender: NewMortgageDetails) => EscrowDetails;
    close: () => void;
}

module.component('notifyLender', {
    template: template,
    // TODO: Alvin to convert the following controller to class style
    // tslint:disable-next-line:no-big-function
    controller: function(
        this: NotifyLenderController,
        $stateParams: PolicyStateParams,
        currentUserService: CurrentUserService,
        modalService: ModalService,
        hippoApiService: HippoApiService,
        $rootScope: angular.IRootScopeService
    ) {
        'ngInject';

        let selectedLenderId = 0;

        this.$onInit = () => {
            const policyIndex = $stateParams.policyIndex;
            this.tooltipText = `Lender information can’t be edited since it’s used for escrow billing. If you need to
                                make any changes, please call us at 800-585-0705`;
            currentUserService.getCustomerPolicyInfoByIndex(policyIndex).then((policyInfo) => {
                this.policyInfo = policyInfo;

                const clonedMortgageDetails = _.cloneDeep(getMortgages(policyInfo));

                this.mortgages = _.keyBy(clonedMortgageDetails, 'id');

                this.showLendersList = _.size(this.mortgages) > 0;

                if (!this.showLendersList) {
                    this.addLender();
                }
            });
        };

        this.showAddLenderButton = () => {
            return (
                _.filter(this.mortgages, (mortgage) => {
                    return !!mortgage;
                }).length < 3
            );
        };

        this.addLender = () => {
            const numberOfExistingMortgages = _.size(this.mortgages);

            const existingIds: Array<number> = _.map<MortgagesCollection, number>(this.mortgages, (mortgage) => {
                if (mortgage) {
                    return parseInt(mortgage.id, 10) as number;
                } else {
                    return 0;
                }
            });

            const lastExistingId: number = _.max(existingIds) || 0;

            selectedLenderId = lastExistingId + 1;

            let type: MortgageDetailsType;

            if (numberOfExistingMortgages === 0) {
                type = 'first';
            } else if (numberOfExistingMortgages === 1) {
                type = 'second';
            } else {
                type = 'third';
            }

            this.showLendersList = false;
            this.selectedLender = {
                isNew: true, // used to track if user hasn't saved yet
                billed: _.size(this.mortgages) === 0,
                city: '',
                id: selectedLenderId.toString(),
                name: '',
                number: '',
                state: '',
                street: '',
                street2: '',
                type: type,
                zip: '',
                loan_officer_contact: '',
                loan_officer_fullname: ''
            };
            this.selectedLenderEscrowDetails = this.getEscrowDetailsFromLender(this.selectedLender);
        };

        this.editLender = (index: number) => {
            this.showLendersList = false;
            const mortgage = this.mortgages[index];

            if (mortgage) {
                this.selectedLender = _.cloneDeep(mortgage);
                this.selectedLenderEscrowDetails = this.getEscrowDetailsFromLender(this.selectedLender);
            } else {
                this.addLender();
            }

            selectedLenderId = index;
        };

        this.deleteLender = (index: number) => {
            this.mortgages[index] = null;
        };

        this.notifyLender = () => {
            this.showSpinner = true;
            const policyId = this.policyInfo.id;
            const policyInfoUpdate: CustomerPolicyInfoUpdate = {
                property_data: {
                    mortgages: {
                        details: {}
                    }
                }
            };

            for (const id in this.mortgages) {
                // Prevents accidental iteration over properties inherited from an object’s prototype.
                if (this.mortgages.hasOwnProperty(id)) {
                    const mortgage = this.mortgages[id];
                    let mortgageKey = id;

                    if (mortgage && mortgage.isNew) {
                        delete mortgage.isNew;
                        mortgageKey = `new${mortgage.id}`;
                    }

                    if (
                        policyInfoUpdate &&
                        policyInfoUpdate.property_data &&
                        policyInfoUpdate.property_data.mortgages
                    ) {
                        policyInfoUpdate.property_data.mortgages.details[mortgageKey] = mortgage;
                    }
                }
            }

            hippoApiService.updateCustomerPolicy(policyId, policyInfoUpdate).then((result) => {
                this.showSpinner = false;
                this.policyInfo = result.data;

                currentUserService.getUpdatedCustomerPolicyInfos().then(() => {
                    $rootScope.$emit('updatedCustomerPolicyInfos');
                });

                modalService.closeAllDialogs();
            });
        };

        this.saveLenderInfo = () => {
            this.showLendersList = true;
            this.selectedLender = {
                ...this.selectedLender,
                name: this.selectedLenderEscrowDetails.lender_name,
                number: this.selectedLenderEscrowDetails.loan_number,
                city: this.selectedLenderEscrowDetails.lender_mailing_address.city,
                state: this.selectedLenderEscrowDetails.lender_mailing_address.state,
                street: this.selectedLenderEscrowDetails.lender_mailing_address.street,
                street2: this.selectedLenderEscrowDetails.lender_mailing_address.street2,
                zip: this.selectedLenderEscrowDetails.lender_mailing_address.zip
            };
            this.mortgages[selectedLenderId] = this.selectedLender;
        };

        this.getEscrowDetailsFromLender = (lender: NewMortgageDetails) => {
            return {
                lender_mailing_address: {
                    city: lender.city,
                    state: lender.state,
                    street: lender.street,
                    street2: lender.street2,
                    zip: lender.zip
                },
                lender_name: lender.name,
                loan_number: lender.number
            };
        };

        this.cancelLenderInfo = () => {
            if (_.isEmpty(this.mortgages)) {
                modalService.closeAllDialogs();
            } else {
                this.showLendersList = true;
            }
        };

        this.close = () => {
            modalService.closeAllDialogs();
        };
    }
});

export { module };
